import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { Tables, TablesInsert, TablesUpdate } from "@/supabase/types"
import { uploadFile } from "./storage/files"
import { normalizeFileName } from '@/lib/utils'
import { getWorkspaceById } from './workspaces'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'

const logger = createIsomorphicLogger('db.files')

export enum UploadStep {
	notStarted,
	scanned,
	sizeChecked,
	fileExists,
	existChecked,
	fileCreated,
	fileWsCreated,
	fileStored,
	fileUpdated,
	fileRetrieved,
	embeddingsResolved,
	embeddingsSaved,
	fileTokenCountSaved,
	validated,
	completed
}

export const getFileById = async (supabase: SupabaseClient<Database>, fileId: string) => {
  const { data: file, error } = await getFileErrorById(supabase, fileId)

  if (!file) {
		const msg = `Error in getFileById for id:${fileId}, msg:${error?.message}`
		logError(logger, error, msg)
    throw new Error(msg)
  }

  return file
}

export const getFileErrorById = async (supabase: SupabaseClient<Database>, fileId: string) => {
  return await supabase
    .from("files")
    .select("*")
    .eq("id", fileId)
    .single()
}

export const getOptionalFileById = async (supabase: SupabaseClient<Database>, fileId: string) => {
  const { data: file, error } = await supabase
    .from("files")
    .select("*")
    .eq("id", fileId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional File by ID', { fileId })
    throw new Error(error.message)
  }

  return file
}

export const getFilesInfo = async (
	supabase: SupabaseClient<Database>,
	ids: string[]
) => {
	const { data, error } = await supabase
		.from('files')
		.select('id, name, description, size, tokens')
		.in('id', ids)

	if (error) throw error
	return data
}

export const getFileWorkspacesByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
    `
      id,
      name,
      files (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
		logError(logger, error, `Error in getFileWorkspacesByWorkspaceId for id:${workspaceId}, msg:${error?.message}`)
    throw new Error(error.message)
  }

  return workspace.files ?? []
}

export const getFilesWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'files'>[]> => {
	const wsFiles = await getFileWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'files'>[] = []

	for (const [i, file] of wsFiles.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "files", context: "ANY", uuid: file.id, tenant:file.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "files", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...file, permissions: perms })
	}

  return result
}

export const getFileWorkspacesByFileId = async (supabase: SupabaseClient<Database>, fileId: string) => {
  const { data: file, error } = await supabase
    .from("files")
    .select(
    `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", fileId)
    .single()

  if (!file) {
		logError(logger, error, `Error in getFileWorkspacesByFileId for id:${fileId}, msg:${error?.message}`)
    throw new Error(error.message)
  }

  return file
}

export const getWorkspaceFileByNameCrc = async (
	supabase: SupabaseClient<Database>,
	workspaceId: string,
	name: string,
	crc: string
): Promise<Tables<"files"> | null> => {
  const { data: fileWorkspace, error } = await supabase
	.from("file_workspaces")
	.select(
	`
		file_id,
		files!inner(*)
	`
	)
	.eq("workspace_id", workspaceId)
	.eq("files.name", name)
	.eq("files.crc", crc)
	.limit(1)
	.maybeSingle()

	if (fileWorkspace) {
		return fileWorkspace.files
	}
	return null
}

export const getAutoShareFiles = async (supabase: SupabaseClient<Database>) => {
  const { data: files, error } = await supabase
    .from("files")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-share Files')
    throw new Error(error.message)
  }

  return files
}

export const getTotalTokens = async (
	supabase: SupabaseClient<Database>,
	ids: string[]
): Promise<number> => {
	const { data, error } = await supabase
		.from('files')
		.select('id, tokens')
		.in('id', ids)

	if (error) throw error
	return data.reduce((sum, file) => sum + (file.tokens ?? 0), 0)
}

export const createFileWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  file_id: string
  workspace_id: string
}) => {
  return await supabase
    .from("file_workspaces")
    .insert([item])
    .select("*")
    .single()
}

export const updateFile = async (
	supabase: SupabaseClient<Database>,
  fileId: string,
  file: TablesUpdate<"files">
) => {
	const { data: updatedAssistant, error } = await updateFileWithError(supabase, fileId, file)
  if (error) {
    logError(logger, error, 'Failed to update File', { fileId, file })
    throw new Error(error.message)
  }

  return updatedAssistant
}

export const updateFileWithError = async (
	supabase: SupabaseClient<Database>,
  fileId: string,
  file: TablesUpdate<"files">
) => {
  return await supabase
    .from("files")
    .update(file)
    .eq("id", fileId)
    .select("*")
    .single()
}

export const createFile = async (
	supabase: SupabaseClient<Database>,
  file: File,
  fileRecord: TablesInsert<"files">,
  workspace_id: string
) => {
	let error:any
	const lastStep = UploadStep.notStarted as UploadStep
	fileRecord.name = normalizeFileName(file.name)

	const workspace = await getWorkspaceById(supabase, workspace_id)

	const fileRecordWithTenant = {
		...fileRecord,
		owning_tenant: workspace!.tenant_id
	}

  const { data: createdFile, error: cfError } = await supabase
    .from("files")
    .insert([fileRecordWithTenant])
    .select("*")
    .single()

  if (cfError) {
		const msg = `Error in createFile for:${fileRecord}, msg:${cfError.message}`
		logError(logger, cfError, msg)
		error = cfError
  }
	else {
		let lastStep = UploadStep.fileCreated
		logger.info(`Completed:${UploadStep[lastStep]}`)

		const { data: createdFileWorkspace, error: cfwError } = await createFileWorkspace(supabase, {
			user_id: createdFile.user_id,
			file_id: createdFile.id,
			workspace_id
		})

		if (cfwError) {
			const msg = `Error in createFileWorkspace for:${fileRecord}, msg:${cfwError.message}`
			logError(logger, cfwError, msg)
			error = cfwError
		}
		else {
			lastStep = UploadStep.fileWsCreated
			logger.info(`Completed:${UploadStep[lastStep]}`)

			const { filePath, error: uploadError } = await uploadFile(supabase, file, {
				name: createdFile.name,
				user_id: createdFile.user_id,
				file_id: createdFile.name
			})

			if (uploadError) {
				const msg = `Error in uploadFile for:${fileRecord}, msg:${uploadError.message}`
				logError(logger, uploadError, msg)
				error = uploadError
			}
			else {
				lastStep = UploadStep.fileStored
				logger.info(`Completed:${UploadStep[lastStep]}`)

				const { data: updatedFile, error: updateError } = await updateFileWithError(supabase, createdFile.id,
					{ file_path: filePath }
				)

				if (updateError) {
					const msg = `Error in updateFile for:${fileRecord}, msg:${updateError.message}`
					logError(logger, updateError, msg)
					error = updateError
				}
				else {
					lastStep = UploadStep.fileUpdated
					logger.info(`Completed:${UploadStep[lastStep]}`)
					const { data: file, error: fileError } = await getFileErrorById(supabase, createdFile.id)

					if (fileError) {
						const msg = `Error in gettingFile for:${fileRecord}, msg:${fileError.message}`
						logError(logger, fileError, msg)
						error = fileError
					}
					else {
						lastStep = UploadStep.fileRetrieved
						logger.info(`Completed:${UploadStep[lastStep]}`)
						return {lastStep, fileRecord:file, error}
					}
				}
			}
		}
	}

	return { lastStep, fileRecord:null, error }
}

/**
 * <b>DO NOT USE</b>
 *
 * This method is broken and will result in unusable crap at the moment. Kept in place since called by import
 * which is not active. But removing could cause import to fail even more badly due to lack of referential
 * integrity.
 *
 * @param {TablesInsert<"files">[]} files
 * @param {string} workspace_id
 * @return {*}
 */
export const createFiles = async (
	supabase: SupabaseClient<Database>,
  files: TablesInsert<"files">[],
  workspace: Tables<"workspaces">
) => {
	const filesWithTenant = files.map(file => ({
		...file,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdFiles, error } = await supabase
    .from("files")
    .insert(filesWithTenant)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Files', { files: filesWithTenant })
    throw new Error(error.message)
  }

  logger.info('Files created successfully', { createdFiles })

  await createFileWorkspaces(supabase,
    createdFiles.map(file => ({
      user_id: file.user_id,
      file_id: file.id,
	    workspace_id: workspace.id
    }))
  )

  return createdFiles
}

export const createFileWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; file_id: string; workspace_id: string }[]
) => {
  const { data: createdFileWorkspaces, error } = await supabase
    .from("file_workspaces")
    .upsert(items, { onConflict: 'file_id, workspace_id' })
    .select("*")

  if (error) {
	  logError(logger, error, `Error in createFileWorkspaces for: ${JSON.stringify(items)}, msg: ${error?.message}`)
		throw new Error(error.message)
	}

  logger.info('File Workspaces created successfully', { createdFileWorkspaces })
  return createdFileWorkspaces
}

export const deleteFile = async (supabase: SupabaseClient<Database>, fileId: string) => {
  const { error } = await supabase.from("files").delete().eq("id", fileId)

  if (error) {
		logError(logger, error, `Error in deleteFile for id:${fileId}, msg:${error?.message}`)
    throw new Error(error.message)
  }

  return true
}

export const deleteFileWorkspace = async (
	supabase: SupabaseClient<Database>,
	fileId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("file_workspaces")
    .delete()
    .eq("file_id", fileId)
    .eq("workspace_id", workspaceId)

  if (error) {
		logError(logger, error, `Error in deleteFileWorkspace for file id: ${fileId}, workspace id: ${workspaceId}, msg: ${error?.message}`)
		throw new Error(error.message)
	}

  return true
}
